import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useMe } from '../hooks/useMe'

// Gates the staff app. If the call to /api/me fails with 401, we redirect
// to the SSO login. If it succeeds but the user has no role (not a member of
// SG-Events-Admins or SG-Events-Users), show a friendly access-denied page.
//
// The /volunteer/* surface is exempt: volunteers authenticate via their own
// magic-link cookie (handled inside VolunteerRoutes), not Entra SSO, so we
// pass those paths straight through rather than bounce them to the staff
// login. Hooks run unconditionally first (engineering lesson 2).
export function SignInGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { data: me, isLoading, error } = useMe()

  if (location.pathname.startsWith('/volunteer')) {
    return <>{children}</>
  }

  // The function-confirm landing page (EM-2) is reached by NYMR staff/managers
  // via an emailed magic link — token-authed, no Entra session. Pass it straight
  // through rather than bounce to SSO. (Trailing slash so it can't match the
  // admin-gated /forecast route itself.)
  if (location.pathname.startsWith('/forecast/function/')) {
    return <>{children}</>
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment">
        <div className="text-ink-soft text-sm font-mono">Loading...</div>
      </div>
    )
  }

  // 401 from the API means SWA didn't recognise our session cookie.
  // Redirect to /.auth/login/aad and let SSO take over.
  const is401 = error && /^401\b/.test(error.message)
  if (is401) {
    window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(
      window.location.pathname + window.location.search
    )}`
    return null
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl mb-3">Something went wrong</h1>
          <p className="text-ink-soft text-sm mb-4">
            We couldn't load your session. The detail below is from the API.
          </p>
          <pre className="bg-white border border-line rounded p-3 text-xs text-left overflow-auto">
            {error.message}
          </pre>
        </div>
      </div>
    )
  }

  if (me && !me.role) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-parchment p-6">
        <div className="max-w-md text-center">
          <h1 className="text-2xl mb-3">Access pending</h1>
          <p className="text-ink-soft text-sm mb-4">
            You're signed in as <strong>{me.upn}</strong>, but you're not yet a
            member of the Events Manager security groups.
          </p>
          <p className="text-ink-soft text-sm mb-4">
            Ask Chris Flynn to add you to <code className="font-mono">SG-Events-Users</code>.
          </p>
          <a href="/.auth/logout" className="text-sm text-oxblood underline">Sign out</a>
        </div>
      </div>
    )
  }

  return <>{children}</>
}